import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { user } from 'ampersand-app'
import download from 'downloadjs'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RefreshIcon from '@mui/icons-material/Refresh'
import SendIcon from '@mui/icons-material/Send'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { alpha } from '@mui/material/styles'
// FLAG:docuAiEnhancements — the same pulsing dots as in the general AI chat.
import DotsLoader from '@/modules/base/components/loader/dots-loader'
// FLAG:docuAiEnhancements — „Analyse-Problem melden" (receipt analysis, target 'docuai'); self-gated,
// renders null without the flag or without a minioId.
import ReportAnalysisProblemButton from '@/modules/document/components/review/report-analysis-problem-button'
// FLAG:docuAiEnhancements — „Antwort melden" (knowledge-base gap, target 'ragchat').
import ReportChatAnswerButton from '@/modules/document/components/document-drawer/report-chat-answer-button'
import config from '@/config'
import lang from '@/lang'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { getChartsOfAccounts } from '@/redux/ducks/taxCategories/main'
import { experimentalFeatures } from '@/modules/base/util/constants'
import { buildRagChatSituation } from '@/modules/document/components/document-drawer/rag-chat-state'
import { buildChatExport, createChatExportBlob } from '@/modules/document/utils/chat-export'
import {
  ChatInputElement,
  useChatInputFocusAfterTurn,
} from '@/modules/document/components/document-drawer/use-chat-input-focus'
// FLAG:docuAiEnhancements — history arithmetic for retries, testable without React.
import { findPrecedingQuestion, findPrecedingQuestionIndex, planRetry } from '@/modules/document/utils/chat-retry'
// FLAG:docuAiEnhancements — sizing arithmetic for resizing the large view, also testable without React.
import {
  CHAT_DIALOG_CORNER_RESERVE,
  ChatDialogSize,
  fitChatDialogToViewport,
  resizeChatDialog,
} from '@/modules/document/utils/chat-dialog-resize'
import { ChatStreamEvent, MAX_CHAT_RETRIES, streamChatWithRetries } from '@/modules/document/utils/chat-stream'
import { TaxCategoryAccount } from '@/modules/document/utils/tax-category-situation'

/**
 * FLAG:docuAiEnhancements — RAG chat ("Belege-Chat" in the UI): collapsible help chat in the document drawer.
 * Asks the DMS llm-gateway RAG endpoint about review / invoice analysis / e-invoice
 * questions. Stateless: the component holds the conversation and sends it as history
 * with every turn; nothing is persisted.
 *
 * The request carries the CONTEXT slug (where the user is — the gateway's registry maps
 * it to knowledge base + triage policy + persona, so this component is reusable on other
 * orgaMAX surfaces via the `context` prop) and a SITUATION block (what is in front of
 * the user — the open document), so deictic questions ("here", "this document")
 * resolve. SSE contract (canonical RAG-chat v1): status → context (citations) →
 * token* (deltas, streamed live into the pending assistant bubble) → done | error,
 * then [DONE] (REPO_INTERACTION.md "omxweb → DMS llm-gateway").
 *
 * Layout: the inline conversation has a FIXED height once messages exist (the section
 * must never grow with the answer and push the review form around — it scrolls inside,
 * scrollbar gutter reserved so streaming does not jitter), and the expand button lifts
 * the same conversation state into a large Dialog for comfortable reading. Assistant
 * answers are markdown (same renderer as the AI chat); "Quelle: [n]" lines are rendered
 * as small annotations.
 */

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  sources?: string[]
  isError?: boolean
}

const chatLang = lang.documents.ragChat
const ERROR_TEXT = chatLang.error
const TITLE = chatLang.title
const EMPTY_HINT = chatLang.emptyHint

const ChatExportButton = ({ disabled, onClick }: { disabled: boolean, onClick: () => void }) => (
  <Tooltip title={chatLang.exportTranscript.action}>
    {/* A span keeps the tooltip reachable while MUI disables pointer events on the button. */}
    <span>
      <IconButton
        size="small"
        aria-label={chatLang.exportTranscript.action}
        disabled={disabled}
        onClick={onClick}
      >
        <DownloadRoundedIcon fontSize="small" />
      </IconButton>
    </span>
  </Tooltip>
)

// The gateway appends citation footnotes as trailing "[n] Quelle: …" lines. Split them
// off so they can be rendered as a small annotation instead of body text.
const splitSourceFootnotes = (content: string) => {
  const lines = content.trimEnd().split('\n')
  const footnotes: string[] = []
  while (lines.length && /^\[\d+\]\s*Quelle:/.test(lines[lines.length - 1].trim())) {
    footnotes.unshift(lines.pop()!.trim())
  }
  return { body: lines.join('\n').trimEnd(), footnotes }
}

/**
 * Bubble shape based on the general AI chat („Max", `base/components/ai/chat`):
 * a large radius with ONE square bottom corner on the speaker's side, a solid-colored answer,
 * and the user's question in white with a border. Previously, the square corner was at the top
 * and the radius was small — two chats in the same application looked different.
 *
 * Deliberately recreated instead of importing
 * `AiChatBubble`/`AiChatAnswer`: both depend on Max's
 * own Redux state (`ducks/ai/chat`) and a tenant-avatar component that does not belong here. The
 * shape is shared, not the state — and `DotsLoader` below is shared code, not a second copy.
 *
 * `dense` is the accordion version (drawer/review), while the full version is the large view.
 */
const bubbleSx = (role: 'user' | 'assistant', isError: boolean, dense: boolean): SxProps<Theme> => ({
  alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
  maxWidth: role === 'user' ? '85%' : '80%',
  width: 'fit-content',
  wordBreak: 'break-word',
  px: dense ? 2 : 2.5,
  py: dense ? 1.25 : 2.25,
  borderRadius: dense ? '20px' : '30px',
  ...(role === 'user'
    ? { borderBottomRightRadius: 0 }
    : { borderBottomLeftRadius: 0 }),
  border: role === 'user' ? '1px solid' : 'none',
  borderColor: 'divider',
  bgcolor: (theme: Theme) => (role === 'user'
    ? theme.palette.common.white
    : (isError
      ? alpha(theme.palette.error.main, 0.08)
      : alpha(theme.palette.primary.main, 0.06))),
})

const markdownSx = (textVariant: 'body1' | 'body2'): SxProps<Theme> => ({
  typography: textVariant,
  color: 'text.primary',
  wordBreak: 'break-word',
  '& p': { m: 0, mb: 1 },
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { m: 0, mb: 1, pl: 2.5 },
  '& li': { mb: 0.5 },
  '& h1, & h2, & h3, & h4': { m: 0, mb: 1, typography: 'subtitle1', fontWeight: 600 },
  '& code': { bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5, fontSize: '0.9em' },
})

// Mid-text citation lines ("Quelle: [1], [3]") arrive as ordinary markdown paragraphs.
// Render them as small secondary annotations so long answers keep a calm rhythm.
const isQuelleLine = (children: React.ReactNode) => {
  const first = Array.isArray(children) ? children[0] : children
  return typeof first === 'string' && first.trimStart().startsWith('Quelle:')
}

const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => (isQuelleLine(children)
    ? <Typography variant="caption" color="text.secondary" component="p" sx={{ display: 'block' }}>{children}</Typography>
    : <p>{children}</p>),
}

/**
 * The user turn an assistant answer replies to — what a report about that answer has to carry.
 *
 * Walks BACKWARDS from the answer instead of pairing by index: the list is not guaranteed to
 * alternate strictly (an error turn adds an assistant message without a question of its own), so
 * `messages[i - 1]` would sometimes attach the wrong question to a report.
 */

const AssistantMarkdown = ({ content, textVariant }: { content: string, textVariant: 'body1' | 'body2' }) => (
  <Box sx={markdownSx(textVariant)}>
    <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</Markdown>
  </Box>
)

const ChatConversation = ({ messages, isStreaming, streamingText, retryAttempt, height, dense = false, minioId, onRetry }: {
  messages: ChatMessage[]
  isStreaming: boolean
  streamingText: string
  retryAttempt: number
  height: number | string
  dense?: boolean
  /** The open document, when there is one — rides along on a reported answer for triage. */
  minioId?: string
  /** FLAG:docuAiEnhancements — repeat the failed turn. Offered ONLY on the last message (see
   * below); without it the error bubble is a dead end and the question has to be retyped. */
  onRetry?: () => void
}) => {
  const listRef = useRef<HTMLDivElement>(null)
  const textVariant = dense ? 'body2' : 'body1'

  // Keep the newest content in view — the container scrolls, the section does not grow.
  // useLayoutEffect (not useEffect) so scrollTop is set BEFORE the browser paints the new
  // token: with a post-paint effect each token is painted at the old scroll position and
  // then jumped to the bottom, which reads as a per-token flicker while streaming.
  useLayoutEffect(() => {
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, isStreaming, streamingText, retryAttempt])

  return (
    <Box
      ref={listRef}
      sx={{
        height,
        // 'scroll' (not 'auto') so the scrollbar is always rendered in the reserved
        // gutter. With 'auto' the bar appears/disappears as the markdown re-parses each
        // streamed token and the content height crosses the overflow threshold — that
        // toggling IS the flickering scrollbar. Always-on + reserved gutter = no toggle,
        // no reflow.
        overflowY: 'scroll',
        scrollbarGutter: 'stable',
        mb: 1,
        pr: 0.5,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {messages.length === 0 && !streamingText && (
        <Typography variant={textVariant} color="text.secondary">{EMPTY_HINT}</Typography>
      )}
      {/* mt:auto pins the conversation to the bottom of the box (chat-style) until it fills it */}
      <Stack spacing={dense ? 1 : 1.5} sx={{ mt: 'auto' }}>
        {messages.map((message, i) => {
          const isUser = message.role === 'user'
          const { body, footnotes } = isUser || message.isError
            ? { body: message.content, footnotes: [] as string[] }
            : splitSourceFootnotes(message.content)
          return (
            <Box key={i} data-qs-id={isUser ? 'rag-chat-question' : 'rag-chat-answer'} sx={bubbleSx(message.role, !!message.isError, dense)}>
              {!isUser && !message.isError
                ? <AssistantMarkdown content={body} textVariant={textVariant} />
                : (
                  <Typography variant={textVariant} sx={{ whiteSpace: 'pre-wrap', color: message.isError ? 'error.main' : 'text.primary' }}>
                    {body}
                  </Typography>
                )}
              {footnotes.length > 0 && (
                <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                  {footnotes.map(footnote => (
                    <Typography key={footnote} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {footnote}
                    </Typography>
                  ))}
                </Box>
              )}
              {!!message.sources?.length && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                  {message.sources.map(source => (
                    <Chip key={source} size="small" variant="outlined" label={source} />
                  ))}
                </Stack>
              )}
              {/* FLAG:docuAiEnhancements — „Antwort melden" on THIS answer, not globally: the report
                  includes this exact question and answer; a single button on the chat would force
                  the user to describe which passage they mean. Not on an error message („Chat nicht
                  erreichbar") — a knowledge gap is not responsible for that.      */}
              {!isUser && !message.isError && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, mr: -0.5 }}>
                  <ReportChatAnswerButton
                    question={findPrecedingQuestion(messages, i)}
                    answer={message.content}
                    minioId={minioId}
                  />
                </Box>
              )}
              {/* FLAG:docuAiEnhancements — „Erneut versuchen" ONLY on the last message. Retrying in
                  the middle of the history appends the new answer at the end and disrupts the order;
                  users who want to ask an older question again can submit it anew. Hidden while a
                  stream is running so a second turn cannot start in parallel. Without a preceding
                  question, there is nothing to retry.                                  */}
              {message.isError
                && !!onRetry
                && i === messages.length - 1
                && !isStreaming
                && findPrecedingQuestionIndex(messages, i) >= 0 && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<RefreshIcon fontSize="small" />}
                    onClick={onRetry}
                  >
                    {chatLang.retry}
                  </Button>
                </Box>
              )}
            </Box>
          )
        })}
        {isStreaming && retryAttempt > 0 && (
          <Box sx={bubbleSx('assistant', false, dense)} role="status" data-qs-id="rag-chat-retry-status">
            <Typography variant={textVariant}>
              {chatLang.retryNotice.replace('{attempt}', String(retryAttempt)).replace('{max}', String(MAX_CHAT_RETRIES))}
            </Typography>
          </Box>
        )}
        {/* Live answer while tokens stream in — becomes a regular message on `done`. */}
        {streamingText && (
          <Box sx={bubbleSx('assistant', false, dense)}>
            <AssistantMarkdown content={streamingText} textVariant={textVariant} />
          </Box>
        )}
        {/* The waiting state now looks like the general
            AI chat: the three pulsing dots are IN the
            answer bubble, exactly where the text will appear — instead of a spinner beside the
            bubble that disappears again and makes the bubble pop up below it. `DotsLoader` is the same component Max uses, including its
            `prefers-reduced-motion` fallback.  */}
        {isStreaming && !streamingText && (
          <Box sx={bubbleSx('assistant', false, dense)} aria-label={chatLang.pending}>
            <DotsLoader />
          </Box>
        )}
      </Stack>
    </Box>
  )
}

const ChatInput = ({ input, disabled, onChange, onSend, inputRef, sendButtonRef, autoFocus = false }: {
  input: string
  disabled: boolean
  onChange: (value: string) => void
  onSend: () => void
  inputRef?: React.Ref<ChatInputElement>
  sendButtonRef?: React.Ref<HTMLButtonElement>
  autoFocus?: boolean
}) => (
  <Stack direction="row" spacing={1} alignItems="flex-end">
    <TextField
      fullWidth
      multiline
      maxRows={3}
      size="small"
      inputProps={{ 'data-qs-id': 'rag-chat-input' }}
      inputRef={inputRef}
      autoFocus={autoFocus}
      placeholder={chatLang.inputPlaceholder}
      value={input}
      disabled={disabled}
      onChange={event => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault()
          onSend()
        }
      }}
    />
    <IconButton
      ref={sendButtonRef}
      color="primary"
      onClick={onSend}
      disabled={disabled || !input.trim()}
      data-qs-id="rag-chat-send"
      aria-label="Frage senden"
    >
      <SendIcon />
    </IconButton>
  </Stack>
)

/**
 * FLAG:docuAiEnhancements — the most recently resized dimensions of the large view, remembered
 * for this browser session.
 *
 * Deliberately a module value rather than state: the component is remounted when the receipt
 * changes (the drawer's `document` is continually replaced), and anyone who enlarged the window
 * once should not have to resize it for the next receipt. Also deliberately NOT persisted — a
 * window size is not a setting that must survive a restart, which would require a place in the
 * user profile.
 */
let rememberedDialogSize: ChatDialogSize | null = null

interface RagChatSectionProps {
  context?: string
  /**
   * FLAG:docuAiEnhancements — the receipt currently being discussed when it does NOT come from
   * the Documents drawer.
   *
   * Until now, the chat lived exclusively in the drawer and therefore read the receipt directly
   * from `state.document.drawer.document`. A tenant
   * without the Documents module (accounting only)
   * has no such drawer — the receipt chat was simply inaccessible even though the tenant reviews
   * the same invoices. Instead of copying the chat, this allows the host to supply its receipt.
   *
   * Without the prop, everything stays as before: the drawer selector decides, so this changes no
   * drawer behavior.
   */
  document?: unknown
}

const RagChatSection = ({ context = 'belege', document: hostDocument }: RagChatSectionProps) => {
  const docuAiEnhancementsEnabled = user?.experimentalFeatures?.includes(experimentalFeatures.docuAiEnhancements)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamText, setStreamText] = useState('')
  const [retryAttempt, setRetryAttempt] = useState(0)
  const [accordionExpanded, setAccordionExpanded] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [completedTurnCount, setCompletedTurnCount] = useState(0)
  const {
    inlineInputRef,
    dialogInputRef,
    inlineSendButtonRef,
    dialogSendButtonRef,
  } = useChatInputFocusAfterTurn(
    completedTurnCount,
    dialogOpen,
    accordionExpanded,
  )
  // FLAG:docuAiEnhancements — null = the default
  // size (maxWidth="md" / 75vh); fixed dimensions are
  // set only after dragging the handle. Until then, the dialog behaves exactly as before.
  const [dialogSize, setDialogSize] = useState<ChatDialogSize | null>(rememberedDialogSize)
  const dialogPaperRef = useRef<HTMLDivElement>(null)
  const resizeStartRef = useRef<{ x: number, y: number, size: ChatDialogSize } | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const dispatch = useAppDispatch()
  // FLAG:docuAiEnhancements — exactly ONE request per chat lifetime, even if the user repeatedly
  // opens and closes the accordion. A ref rather than state: loading must not trigger a render;
  // the store updates by itself as soon as it is populated.
  const taxCategoriesRequestedRef = useRef(false)

  // Situation ("what is in front of the user") from the drawer state — lets the chat
  // resolve "here"/"this document" AND answer questions about the document itself
  // (analysis state, XML vs. paper, validity verdict, extracted values, gaps) instead of
  // asking the reviewer to read the screen back to it. Whitelisted, derived read-only from
  // the document the drawer already holds; the server re-applies the whitelist.
  // The selector continues to run unchanged — hooks must not be called conditionally, and it IS the
  // source for the drawer. A receipt explicitly supplied by the host takes precedence: only the host
  // knows what is in front of the user when no drawer is open.
  const drawerDocument = useAppSelector(
    (state: { document?: { drawer?: { document?: unknown } } }) => state.document?.drawer?.document,
  )
  const openDocument = hostDocument ?? drawerDocument
  // Tenant tax categories are read from Redux and requested when the chat is opened
  // (ensureTaxCategoriesLoaded), independently of the review form.
  const taxCategoryAccounts = useAppSelector(
    (state: { taxCategories?: { main?: { rawCategories?: TaxCategoryAccount[] } } }) =>
      state.taxCategories?.main?.rawCategories,
  )
  const situation = buildRagChatSituation(
    !!docuAiEnhancementsEnabled,
    openDocument,
    chatLang.viewContext,
    { taxCategoryAccounts },
  )
  // Rides along on a reported answer so an issue raised next to an open receipt can be investigated
  // WITH that receipt. Absent on surfaces without one — a knowledge-base gap needs no document.
  const openDocumentMinioId = typeof (openDocument as { minioId?: unknown })?.minioId === 'string'
    ? (openDocument as { minioId: string }).minioId
    : undefined

  const openDocumentId = (openDocument as { id?: string | number })?.id
  // Cancel the entire turn, including its pending retry timer, on document/context change.
  useEffect(() => {
    setMessages([])
    setInput('')
    setIsStreaming(false)
    setStreamText('')
    setRetryAttempt(0)
    return () => {
      abortRef.current?.abort()
      abortRef.current = null
    }
  }, [openDocumentId, openDocumentMinioId, context, docuAiEnhancementsEnabled])

  useEffect(() => {
    if (!accordionExpanded && !dialogOpen) abortRef.current?.abort()
  }, [accordionExpanded, dialogOpen])

  if (!docuAiEnhancementsEnabled) return null

  /**
   * FLAG:docuAiEnhancements — load the tenant's tax categories independently instead of relying
   * on the review component.
   *
   * The selector above read the store that the review used to populate incidentally. Since that
   * refactor, there is no tax-category component there (it will return later), and the only
   * remaining caller on this path — `receipt-form.tsx` — loads in its effect's CLEANUP, when the
   * form is LEFT. In a fresh session the list is therefore empty, `situation.taxCategories`
   * silently disappears, and the chat gives only generic knowledge-base examples instead of the
   * tenant's categories. That was the regression (“the correct tax categories were available here
   * before our refactor”).
   *
   * The original reason against loading independently — “no chart-of-accounts request for a
   * question the user may never ask” — remains valid: the call is tied to opening
   * the CHAT, not the drawer. Anyone who never chats never triggers a request. The coalescer in
   * `getChartsOfAccounts` (flag-ON) catches duplicate requests anyway.
   */
  const ensureTaxCategoriesLoaded = () => {
    if (taxCategoriesRequestedRef.current) return
    if (taxCategoryAccounts?.length) return
    taxCategoriesRequestedRef.current = true
    dispatch(getChartsOfAccounts())
  }

  const viewport = () => ({ width: window.innerWidth, height: window.innerHeight })

  const openFullView = () => {
    // The large view can be opened without the accordion ever having been expanded — otherwise the
    // chat there would lack the tenant's categories.
    ensureTaxCategoriesLoaded()
    // The browser window may have become smaller between two openings.
    if (rememberedDialogSize) setDialogSize(fitChatDialogToViewport(rememberedDialogSize, viewport()))
    setDialogOpen(true)
  }

  /**
   * FLAG:docuAiEnhancements — resize the large view from its bottom-right corner.
   *
   * The fixed height of 75vh is enough for short answers; as soon as the chat lists positions,
   * tax rows, or a longer explanation, they are read through a peephole. Resizing instead of
   * „Vollbild": users who need the receipt beside it should be allowed to keep it there.
   *
   * Pointer events (not mouse events) — so the same behavior applies to pen and touch. With
   * `setPointerCapture`, all subsequent movements
   * go to the handle even after the pointer leaves
   * it; no listeners on document are needed, and the browser releases capture on pointerup.
   */
  const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
    const paper = dialogPaperRef.current
    if (!paper) return
    // Initial dimensions come from the DOM rather than state: no state exists on the first drag, and
    // the default size depends on maxWidth/vh, so it is known only after rendering.
    const rect = paper.getBoundingClientRect()
    resizeStartRef.current = { x: event.clientX, y: event.clientY, size: { width: rect.width, height: rect.height } }
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const moveResize = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = resizeStartRef.current
    if (!start) return
    const size = resizeChatDialog(start.size, { dx: event.clientX - start.x, dy: event.clientY - start.y }, viewport())
    rememberedDialogSize = size
    setDialogSize(size)
  }

  const endResize = () => {
    resizeStartRef.current = null
  }

  /**
   * One conversation turn. `priorMessages` is the history it builds on — the existing history
   * for normal sending, or the state BEFORE the failed question for a retry. This makes a retry
   * replace the error bubble instead of appending a second round, and the user's question remains
   * exactly once.
   */
  const runTurn = async(question: string, priorMessages: ChatMessage[]) => {
    if (!question || isStreaming || abortRef.current) return

    const history = priorMessages
      .filter(m => !m.isError)
      .map(({ role, content }) => ({ role, content }))
    // Capture the same context/history for every attempt of this turn.
    const body = JSON.stringify({ question, history, context, situation })
    const abort = new AbortController()
    abortRef.current = abort
    const isCurrent = () => abortRef.current === abort
    setMessages([...priorMessages, { role: 'user', content: question }])
    setIsStreaming(true)
    setStreamText('')
    setRetryAttempt(0)
    let sources: string[] = []

    const pushAssistant = (content: string, isError = false) => {
      if (!isCurrent()) return
      setStreamText('')
      setMessages(prev => [...prev, {
        role: 'assistant', content, sources: isError ? [] : sources.slice(0, 5), isError,
      }])
    }
    const handleEvent = (event: ChatStreamEvent) => {
      if (!isCurrent() || abort.signal.aborted) return
      if (event.type === 'context') {
        const seen = new Set<string>()
        sources = (event.data?.sources || [])
          .map(s => s?.source)
          .filter((s): s is string => !!s && !seen.has(s) && !!seen.add(s))
      } else if (event.type === 'token') {
        const token = event.data?.token
        if (token) setStreamText(prev => prev + token)
      } else if (event.type === 'done') {
        pushAssistant(event.data?.reply || '')
      }
    }

    try {
      await streamChatWithRetries({
        signal: abort.signal,
        request: () => fetch(config.resourceHost + 'ragchat/stream', {
          method: 'POST',
          signal: abort.signal,
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + user.token },
          body,
        }),
        onEvent: handleEvent,
        onRetry: (attempt) => {
          if (!isCurrent()) return
          sources = []
          setStreamText('')
          setRetryAttempt(attempt)
        },
      })
    } catch {
      pushAssistant(abort.signal.aborted ? chatLang.cancelled : ERROR_TEXT, true)
    } finally {
      // An old document's turn must not clear a newly started request or move its focus.
      if (isCurrent()) {
        abortRef.current = null
        setIsStreaming(false)
        setStreamText('')
        setRetryAttempt(0)
        setCompletedTurnCount(count => count + 1)
      }
    }
  }

  const send = () => {
    const question = input.trim()
    if (!question || isStreaming) return
    setInput('')
    return runTurn(question, messages)
  }

  /**
   * FLAG:docuAiEnhancements — retry the most recent failed turn.
   *
   * Cut BEFORE the failed question: `runTurn` appends it again itself, and the error bubble
   * disappears in the process. Deliberately a complete new call — the client cannot retrieve an
   * answer that was already generated, and one turn costs one model call. Therefore the button
   * appears only on the last message, not on every message.
   */
  const retryLast = () => {
    if (isStreaming) return
    const plan = planRetry(messages)
    if (!plan) return
    return runTurn(plan.question, plan.priorMessages)
  }

  const exportTranscript = () => {
    const file = buildChatExport(messages, chatLang.exportTranscript)
    if (!file) return
    download(createChatExportBlob(file), file.fileName, file.mimeType)
  }

  const exportDisabled = messages.length === 0 || isStreaming

  return (
    <>
      {/* FLAG:docuAiEnhancements — expanding IS the signal “the chat is now being used”; it
          triggers the one-time loading of tax categories. The accordion remains uncontrolled;
          `onChange` does not alter that.                 */}
      <Accordion
        disableGutters
        elevation={0}
        square
        sx={{ borderTop: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
        onChange={(_event, expanded) => {
          setAccordionExpanded(expanded)
          if (expanded) ensureTaxCategoriesLoaded()
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
            <HelpOutlineIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>{TITLE}</Typography>
            {/* FLAG:docuAiEnhancements — „Analyse-Problem melden" directly in the chat header. A
                thumbs-down on an answer reports a KNOWLEDGE GAP (target 'ragchat'); this reports
                what the ANALYSIS got wrong on the receipt (target 'docuai') — two different
                worklists. This is most noticeable while chatting about a receipt, hence the short
                path from here. `stopPropagation`, or the click closes the accordion. Without minioId
                and without the flag, the component renders null.                         */}
            <Box onClick={(event) => event.stopPropagation()} sx={{ display: 'flex' }}>
              <ReportAnalysisProblemButton minioId={openDocumentMinioId} />
              <ChatExportButton disabled={exportDisabled} onClick={exportTranscript} />
            </Box>
            <Tooltip title={chatLang.openFullView}>
              <IconButton
                size="small"
                data-qs-id="rag-chat-open-full"
                aria-label={chatLang.openFullView}
                onClick={(event) => {
                  event.stopPropagation()
                  openFullView()
                }}
              >
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {/* Fixed height once a conversation exists: the drawer form below must not move. */}
          <ChatConversation
            messages={messages}
            isStreaming={isStreaming}
            streamingText={streamText}
            retryAttempt={retryAttempt}
            height={messages.length === 0 && !streamText ? 'auto' : 220}
            dense
            minioId={openDocumentMinioId}
            onRetry={retryLast}
          />
          <ChatInput
            input={input}
            disabled={isStreaming}
            onChange={setInput}
            onSend={send}
            inputRef={inlineInputRef}
            sendButtonRef={inlineSendButtonRef}
          />
        </AccordionDetails>
      </Accordion>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          ref: dialogPaperRef,
          // Without a manual drag on the handle, every row retains the previous default. Once
          // dragged, fixed dimensions replace it — and `maxWidth` must change too, or the 900 px from
          // maxWidth="md" would cap resizing. The new upper bound matches the drag calculation, so
          // the handle stays to the left of the AI chat button in the window corner even if the
          // browser window becomes smaller AFTER resizing (then only this CSS boundary applies; no
          // pointer event occurs again).
          sx: dialogSize
            ? {
              width: dialogSize.width,
              height: dialogSize.height,
              maxWidth: `calc(100% - ${2 * CHAT_DIALOG_CORNER_RESERVE}px)`,
            }
            : { height: '75vh' },
        }}
        aria-label={TITLE}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <HelpOutlineIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>{TITLE}</Typography>
            {/* Twin of the button in the accordion header — users working in the large view should
                not have to close it first. No stopPropagation needed: the dialog title row expands
                or collapses nothing.                        */}
            <ReportAnalysisProblemButton minioId={openDocumentMinioId} />
            <ChatExportButton disabled={exportDisabled} onClick={exportTranscript} />
            <Tooltip title={chatLang.closeFullView}>
              <IconButton size="small" aria-label={chatLang.closeFullView} onClick={() => setDialogOpen(false)}>
                <CloseFullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogTitle>
        {/* `overflow: hidden` is NOT a cosmetic fix here, but the actual fix: MUI's DialogContent
            scrolls by itself by default (`overflow-y: auto`). Together with the scrolling
            ChatConversation inside it, this created TWO
            nested scroll containers — and because the
            content grows with every token while streaming, the outer one repeatedly crossed its
            overflow threshold: scrollbar appears, disappears, appears. That is exactly the flicker
            in the large view. The accordion was never affected because it has no second container.

            Scrolling belongs to ChatConversation (`overflowY: 'scroll'` +
            `scrollbarGutter: 'stable'`, i.e. a fixed gutter and no reflow on transitions). The dialog
            now supplies only the height.      */}
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', pt: 0, overflow: 'hidden' }}>
          <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <ChatConversation messages={messages} isStreaming={isStreaming} streamingText={streamText} retryAttempt={retryAttempt} height="100%" minioId={openDocumentMinioId} onRetry={retryLast} />
          </Box>
          <ChatInput
            input={input}
            disabled={isStreaming}
            onChange={setInput}
            onSend={send}
            inputRef={dialogInputRef}
            sendButtonRef={dialogSendButtonRef}
            autoFocus
          />
        </DialogContent>
        {/* FLAG:docuAiEnhancements — handle at bottom right, the same shape as on the AI chat window
            („Max", `base/components/ai/chat`): two lines in the corner, `nwse-resize` as the cursor.
            Positioned absolutely in the paper (which is `position: relative`), so outside the flow
            and therefore outside the scrolling conversation. Hidden at mobile width — there the dialog
            already fills the window and there is nothing to resize.       */}
        <Box
          onPointerDown={startResize}
          onPointerMove={moveResize}
          onPointerUp={endResize}
          onPointerCancel={endResize}
          title={chatLang.resizeHint}
          sx={{
            position: 'absolute',
            right: 2,
            bottom: 2,
            width: 18,
            height: 18,
            cursor: 'nwse-resize',
            // Otherwise the browser scrolls/zooms on touch instead of resizing the window.
            touchAction: 'none',
            display: { xs: 'none', sm: 'block' },
            '&::after': {
              content: '""',
              position: 'absolute',
              right: 3,
              bottom: 3,
              width: 9,
              height: 9,
              borderRight: '2px solid',
              borderBottom: '2px solid',
              // `text.secondary`, not `text.disabled`: the 9 px corner is the only indication that
              // this can be dragged — when greyed out, it is invisible.
              borderColor: 'text.secondary',
            },
          }}
        />
      </Dialog>
    </>
  )
}

export default RagChatSection
